import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    IconButton,
    FormControlLabel,
    Grid,
    Switch,
    Typography,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useStyles } from "./Configuration.styles";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import moment from "moment";
import { NoData } from "../../components";
import { ModalProductionLines } from "pages/Corporate/CorporateSetupDashboard/ProductionLines/components";
import useRolesPermissions from "hooks/useRolesPermissions";
import CheckUserPasswordModal from "components/CheckUserPasswordModal";
import ChangeHMICodeWarningModal from "./ChangeHMICodeWarningModal/ChangeHMICodeWarningModal.view";
import { EmailNotificationTypesEnum } from "constants/constants";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { createTimeIntervalsOptions } from "utilities/utilities";
import { UsersEntity } from "entities/Users/UsersEntity";

const EMPTY_HMI_CODE = "$$EMPTY_HMI_CODE$$";

export const Configuration = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const routeParams = useParams();
    const [open, setOpen] = useState(false);
    const [hmiCodeLoading, setHmiCodeLoading] = useState({
        loading: false,
        currentHmiCode: "",
    });
    const [newHMICode, setNewHMICode] = useState<string | null>();
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const machineCenterId = useMemo(() => Number(routeParams.machineCenterId) || 0, [routeParams.machineCenterId]);
    const [hmiCodeToRefresh, setHMICodeToRefresh] = useState<string | null>();
    const { machineCenter, refetch } = MachineCentersEntity.GetMachineCenterDetailsBO(machineCenterId);
    const { updateMachineCenterMutation } = MachineCentersEntity.UpdateMachineCenterCorporateBO();
    const { updateMachineCenterSettingMutation } = MachineCentersEntity.UpsertMachineCenterNotificationSettingBO();
    const { machineCenter: machineCenterHmiCode } = MachineCentersEntity.SubscribeMachineCenterHMICode(machineCenterId);
    const { enableMachineCenterNotification } = UsersEntity.EnableUserMachineCenterNotificationSetting();
    const [showChangeHMICodeWarningModal, setShowChangeHMICodeWarningModal] = useState(false);
    const { enabledNotificationsIds, currentUser } = useContext(CorporateContext);

    const timeIntervalOptions = useMemo(() => createTimeIntervalsOptions() || [], []);
    const corporateNotificationSettingEnabled = useMemo(
        () => enabledNotificationsIds.includes(EmailNotificationTypesEnum.MACHINE_CENTER_DOWNTIME_TRIGGER),
        [enabledNotificationsIds],
    );
    const notificationSettingEnabled = useMemo(
        () => machineCenter[0]?.email_notification_machine_center_settings[0]?.is_enabled,
        [machineCenter],
    );
    const notificationTriggerTime = useMemo(
        () => machineCenter[0]?.email_notification_machine_center_settings[0]?.trigger_time || timeIntervalOptions[0],
        [machineCenter, timeIntervalOptions],
    );

    const handleOpen = () => setOpen(true);
    const handleClose = useCallback(() => {
        refetch();
        setOpen(false);
    }, [refetch, setOpen]);

    const handleIsEnabledChange = useCallback(
        async (isEnabled: boolean) => {
            try {
                const response = await updateMachineCenterMutation({
                    variables: { machineCenterId: machineCenterId, changes: { is_enabled: isEnabled } },
                });
                if (response) {
                    if (!isEnabled) handleClose();
                    else refetch();
                }
            } catch {
                return;
            }
        },
        [updateMachineCenterMutation, handleClose, machineCenterId, refetch],
    );

    const handleNotificationSettingChange = useCallback(
        async (isEnabled: boolean, triggerTime: string, settingId?: number) => {
            try {
                const response = await updateMachineCenterSettingMutation({
                    variables: {
                        notificationSetting: [
                            {
                                ...(settingId ? { id: settingId } : {}),
                                machine_center_id: machineCenterId,
                                trigger_time: triggerTime,
                                notification_type_id: EmailNotificationTypesEnum.MACHINE_CENTER_DOWNTIME_TRIGGER,
                                is_enabled: isEnabled,
                            },
                        ],
                    },
                });
                await enableMachineCenterNotification({
                    variables: {
                        where: {
                            user_notification_setting: {
                                user_id: { _eq: currentUser?.user_id },
                            },
                            machine_center_id: { _eq: machineCenterId },
                        },
                        change: isEnabled,
                    },
                });
                if (response) {
                    await refetch();
                }
            } catch {
                return;
            }
        },
        [
            updateMachineCenterSettingMutation,
            machineCenterId,
            refetch,
            currentUser?.user_id,
            enableMachineCenterNotification,
        ],
    );

    const handleRefresHMICode = useCallback(
        async (currentCode: string) => {
            setHmiCodeLoading({
                currentHmiCode: currentCode,
                loading: true,
            });
            try {
                await updateMachineCenterMutation({
                    variables: { machineCenterId: machineCenterId, changes: { hmi_code_refresh: true } },
                });
                setShowChangeHMICodeWarningModal(true);
            } catch {
                setHmiCodeLoading({
                    currentHmiCode: "",
                    loading: false,
                });
                return;
            }
        },
        [updateMachineCenterMutation, machineCenterId],
    );

    useEffect(() => {
        if (machineCenterHmiCode[0]?.hmi_code !== hmiCodeLoading.currentHmiCode) {
            setHmiCodeLoading({
                currentHmiCode: "",
                loading: false,
            });
        }
    }, [machineCenterHmiCode, hmiCodeLoading.currentHmiCode]);

    useEffect(() => {
        if (
            machineCenterHmiCode[0]?.hmi_code !== hmiCodeLoading.currentHmiCode &&
            !newHMICode &&
            !hmiCodeLoading.loading &&
            !!showChangeHMICodeWarningModal
        ) {
            setNewHMICode(machineCenterHmiCode[0]?.hmi_code);
        }
    }, [
        hmiCodeLoading.currentHmiCode,
        hmiCodeLoading.loading,
        machineCenterHmiCode,
        newHMICode,
        showChangeHMICodeWarningModal,
    ]);

    return machineCenter.length ? (
        <Box className={classes.container}>
            <Box className={classes.detailsContainer}>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <Box className={classes.textContainer}>
                            <Typography className={classes.label}>
                                {t("corporate_backoffice.machine_center_details.date_added")}
                            </Typography>
                            <Typography className={classes.text}>
                                {moment(machineCenter[0]?.created_at).format("MM/DD/YYYY")}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box className={classes.textContainer}>
                            <Typography className={classes.label}>
                                {t("corporate_backoffice.machine_center_details.last_updated")}
                            </Typography>
                            <Typography className={classes.text}>
                                {machineCenter[0]?.updated_at
                                    ? moment(machineCenter[0]?.updated_at).format("MM/DD/YYYY")
                                    : t("corporate_backoffice.machine_center_details.no_update")}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Grid className={classes.gridBorderTop} container spacing={2}>
                    {CORPORATE_PERMISSIONS?.corporationPermissions?.showHmiCode && (
                        <Grid item className={classes.gridItemStyle} xs={3}>
                            <Box>
                                <Typography className={classes.label}>
                                    {t("corporate_backoffice.machine_center_details.hmi_code")}
                                </Typography>
                                <Typography className={classes.text}>
                                    {machineCenterHmiCode[0]?.hmi_code
                                        ? machineCenterHmiCode[0]?.hmi_code
                                        : t("corporate_backoffice.machine_center_details.no_hmi_code")}{" "}
                                </Typography>
                            </Box>
                            {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters &&
                                CORPORATE_PERMISSIONS?.corporationPermissions?.canRefreshHMICode && (
                                    <IconButton
                                        className={classes.refreshButton}
                                        disabled={hmiCodeLoading.loading}
                                        onClick={() =>
                                            setHMICodeToRefresh(machineCenterHmiCode[0]?.hmi_code || EMPTY_HMI_CODE)
                                        }
                                    >
                                        {hmiCodeLoading.loading ? (
                                            <CircularProgress className={classes.loadingContainer} color="info" />
                                        ) : (
                                            <RefreshIcon className={classes.refreshIcon} />
                                        )}
                                    </IconButton>
                                )}
                            <CheckUserPasswordModal
                                open={!!hmiCodeToRefresh}
                                onConfirmPassword={() => handleRefresHMICode(machineCenterHmiCode[0]?.hmi_code || "")}
                                onClose={() => setHMICodeToRefresh(null)}
                                title={t(
                                    "corporate_backoffice.machine_center_details.confirm_refresh_hmi_code_modal_title",
                                )}
                                question={t(
                                    "corporate_backoffice.machine_center_details.confirm_refresh_hmi_code_modal_question",
                                )}
                                questionConfirmedButtonText={t(
                                    "corporate_backoffice.machine_center_details.confirm_refresh_hmi_code_modal_title",
                                )}
                            />
                        </Grid>
                    )}
                    <Grid item xs={3}>
                        <FormControlLabel
                            className={classes.switchLabel}
                            value="start"
                            control={
                                <Switch
                                    disabled={!CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters}
                                    checked={machineCenter[0]?.is_enabled}
                                    className={classes.switch}
                                    onChange={async (e) => {
                                        if (machineCenter[0]?.is_enabled) handleOpen();
                                        else await handleIsEnabledChange(true);
                                    }}
                                />
                            }
                            label={t("corporate_backoffice.machine_center_details.hmi_enabled")}
                            labelPlacement="start"
                        />
                    </Grid>
                    {corporateNotificationSettingEnabled && (
                        <>
                            <Grid item xs={3}>
                                <FormControlLabel
                                    className={classes.switchLabel}
                                    value="start"
                                    control={
                                        <Switch
                                            disabled={
                                                !CORPORATE_PERMISSIONS?.corporationPermissions
                                                    ?.canToggleMachineCenterNotifications
                                            }
                                            checked={notificationSettingEnabled}
                                            className={classes.switch}
                                            onChange={async (e) => {
                                                await handleNotificationSettingChange(
                                                    e.target.checked,
                                                    notificationTriggerTime,
                                                    machineCenter[0]?.email_notification_machine_center_settings[0]?.id,
                                                );
                                            }}
                                        />
                                    }
                                    label={t("corporate_backoffice.machine_center_details.downtime_notification")}
                                    labelPlacement="start"
                                />
                            </Grid>
                            <Grid
                                style={{
                                    textAlign: "start",
                                    paddingTop: "15px",
                                }}
                                item
                                xs={3}
                            >
                                <Typography className={classes.label}>
                                    {t("corporate_backoffice.machine_center_details.notification_trigger")}
                                </Typography>
                                <FormControl variant="standard" sx={{ maxWidth: 90 }}>
                                    <Select
                                        disabled={
                                            !notificationSettingEnabled ||
                                            !CORPORATE_PERMISSIONS?.corporationPermissions
                                                ?.canToggleMachineCenterNotifications
                                        }
                                        disableUnderline
                                        className={classes.select}
                                        labelId="demo-simple-select-standard-label"
                                        id="demo-simple-select-standard"
                                        value={notificationTriggerTime}
                                        onChange={async (e) => {
                                            await handleNotificationSettingChange(
                                                notificationSettingEnabled,
                                                e.target.value,
                                                machineCenter[0]?.email_notification_machine_center_settings[0]?.id,
                                            );
                                        }}
                                    >
                                        {timeIntervalOptions.map((interval) => (
                                            <MenuItem key={interval} className={classes.menuItem} value={interval}>
                                                {interval}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>{" "}
                        </>
                    )}
                </Grid>
                <Box className={classes.textContainerSeparator}>
                    <Typography className={classes.label}>
                        {t("corporate_backoffice.machine_center_details.name")}
                    </Typography>
                    <Typography className={classes.text}>{machineCenter[0]?.name}</Typography>
                </Box>
                <Box className={classes.textContainer}>
                    <Typography className={classes.label}>
                        {t("corporate_backoffice.machine_center_details.manufacturer")}
                    </Typography>
                    <Typography className={classes.text}>
                        {machineCenter[0]?.manufacturer
                            ? machineCenter[0]?.manufacturer
                            : t("corporate_backoffice.machine_center_details.no_manufacturer")}
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <Box className={classes.textContainer}>
                            <Typography className={classes.label}>
                                {t("corporate_backoffice.machine_center_details.model_number")}
                            </Typography>
                            <Typography className={classes.text}>
                                {machineCenter[0]?.model_number
                                    ? machineCenter[0]?.model_number
                                    : t("corporate_backoffice.machine_center_details.no_model_number")}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box className={classes.textContainer}>
                            <Typography className={classes.label}>
                                {t("corporate_backoffice.machine_center_details.bottleneck")}
                            </Typography>
                            <Typography className={classes.text}>
                                {machineCenter[0]?.is_bottleneck
                                    ? t("corporate_backoffice.machine_center_details.affirmative_answer")
                                    : t("corporate_backoffice.machine_center_details.negative_answer")}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Grid className={classes.gridContainerSeparator} container spacing={2}>
                    <Grid item xs={3}>
                        <Box className={classes.textContainer}>
                            <Typography className={classes.label}>
                                {t("corporate_backoffice.machine_center_details.downtime_data_capture")}
                            </Typography>
                            <Typography className={classes.text}>
                                {machineCenter[0]?.downtime_method_capture?.name}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box className={classes.textContainer}>
                            <Typography className={classes.label}>
                                {t("corporate_backoffice.machine_center_details.plc_address")}
                            </Typography>
                            <Typography className={classes.text}>
                                {machineCenter[0]?.plc_address
                                    ? machineCenter[0]?.plc_address
                                    : t("corporate_backoffice.machine_center_details.no_plc_address")}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <Box className={classes.textContainer}>
                            <Typography className={classes.label}>
                                {t("corporate_backoffice.machine_center_details.minimum_downtime_definition")}
                            </Typography>
                            <Typography className={classes.text}>
                                {machineCenter[0]?.minimum_downtime}{" "}
                                {t("corporate_backoffice.machine_center_details.seconds")}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box className={classes.textContainer}>
                            <Typography className={classes.label}>
                                {t("corporate_backoffice.machine_center_details.downtime_trigger")}
                            </Typography>
                            <Typography className={classes.text}>
                                {machineCenter[0]?.downtime_trigger_threshold}{" "}
                                {t("corporate_backoffice.machine_center_details.seconds")}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <ModalProductionLines open={open} handleClose={handleClose} title={"Disable Machine Center"}>
                <div className={classes.modalContentContainer}>
                    <Typography className={classes.modalText}>
                        {t(
                            "corporate_backoffice.production_line_view.machine_center_creation_form.disable_confirmation_message",
                        )}
                    </Typography>
                    <div className={classes.buttonsContainer}>
                        <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                            {t("corporate_backoffice.production_line_view.machine_center_creation_form.cancelButton")}
                        </Button>
                        <Button
                            className={classes.addButton}
                            variant="outlined"
                            onClick={() => handleIsEnabledChange(false)}
                        >
                            {t("corporate_backoffice.production_line_view.machine_center_creation_form.confirm_button")}
                        </Button>
                    </div>
                </div>
            </ModalProductionLines>
            <ChangeHMICodeWarningModal
                open={showChangeHMICodeWarningModal && !!newHMICode}
                machineCenter={machineCenter[0] || {}}
                onClose={() => {
                    setNewHMICode(null);
                    setShowChangeHMICodeWarningModal(false);
                }}
                newHMICode={newHMICode || ""}
            />
        </Box>
    ) : (
        <NoData />
    );
};
